// src/com/trans_plan_launch/TransPlanUploader.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/system';
//import { styled } from '@mui/material/styles';
import {
    Button, DialogContent, DialogActions, CircularProgress, Tooltip
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close'; // close
import FindInPageIcon from '@mui/icons-material/FindInPage'; // find file
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant'; // error
import { AppWord, AppStyle, CodeGroup } from '../../value/AppConst';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import DraggableEditor from '../../common/DraggableEditor';
import { useMutation, useReactiveVar } from '@apollo/client';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import {
    arriveInfoListToSelectStoreRepo, authTableTreeRepo, codeTreeRepo,
    comInfoListRepo, userInfoRepo, vehTypeGroupOrderListByDcRepo
} from '../../value/AppModel';
import FlexyTable from '../../common/flexytable/FlexyTable';
import {
    checkTransPlanAndMap, reformTransPlanToUpload, TransPlanCsvMap, TransPlanUploadDictionary
} from './TransPlanUploadDict';
import SearchBox from '../../common/SearchBox';
import { enqueueSnackbar } from 'notistack';
import { ContentConainer, ContentHeader, ControlBox, CountLabel, ToolBox } from '../../common/TopViewControl';
import IsValid from '../../value/IsValid';
import ValueMisc from '../../value/ValueMisc';
import { usePapaParse } from 'react-papaparse';
import VzmCheckbox from '../../common/VzmCheckbox';
import { UPLOAD_TRANS_PLAN } from '../trans_plan/TransPlanGql';

const tableProps = TransPlanUploadDictionary.tableProps;

const uploadMap = TransPlanCsvMap;
const EmptyCsvData = {data:[], errors:[], meta:{}};

export default function TransPlanUploader({
    open,
    onClose, // uploaded: Boolean
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const codeTree = useReactiveVar(codeTreeRepo);
    const storeList = useReactiveVar(arriveInfoListToSelectStoreRepo); // 점포 검사.
    const vehTypeGroupOrderListByDc = useReactiveVar(vehTypeGroupOrderListByDcRepo); // veh type 검사.
    const comInfoListData = useReactiveVar(comInfoListRepo); // comId 검사.
    const [text, setText] = useState('');
    const [useEucKr, setUseEucKr] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [bindData, setBindData] = useState([]);
    const [csvData, setCsvData] = useState(EmptyCsvData); // {data:[], errors:[], meta:{}}
    const [errorCount, setErrorCount] = useState(0);
    const [changed, setChanged] = useState(false);
    const [msgDone, setMsgDone] = useState('');
    const [searchText, setSearchText] = useState('');
    const [filterError, setFilterError] = useState(false);
    const [filterBind, setFilterBind] = useState(false);
    const [countToDisplay, setCountToDisplay] = useState(0);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const checkAuth = useAuthorizationCheck();
    const openConfirmDialog = useConfirmDialog();
    const { readString } = usePapaParse();
    const addAlert = useAlertSetter();
    const fileInputRef = useRef(null);
    // const waveCodeRef = useRef({});
    const waveCodeMap = useMemo(()=>{
        // wave1, wave2, wave3 to codeId
        const waveCodes = ValueMisc.getCodes(codeTree, CodeGroup.TRANS_TYPE_CD);
        const wmap = {};
        waveCodes.forEach((code)=>{
            wmap[code.codeName] = code.codeId;
        });
        return wmap;
    }, [codeTree])
    const uploadedRef = useRef(false);
    // ##################### GQL #####################
    const [uploadTransPlan, responseUpload] = useMutation(UPLOAD_TRANS_PLAN, {
        onCompleted: (data, option) => {onCompletedUpload(data, option);},
        onError: (error) => {
            addAlert({title: "운송요청 업로드 오류", message: [error.message]});
        }
    });

    useEffect(()=>{
        if(!open) {
            setTableData([]);
            setBindData([]);
            setErrorCount(0);
            setChanged(false);
            setMsgDone('');
            setText('');
            setCsvData(EmptyCsvData);
        }
    }, [open]);

    useEffect(()=>{
        if(!IsValid.nullString(text)) {
            readString(text, {
                worker: true,
                complete: (result) => {
                    const { data, errors, meta } = result;
                    data.shift(); // remove header.
                    const records
                    = data.filter((row) => /^\d/.test(row[0]))
                    .map((row) => {
                        const newRow = row.slice(0, Object.keys(uploadMap).length); // 사용할 칼럼 수만큼 자른다. 확인!!
                        // 추가작업 필요하면 여기에. (데이터 변경, 계산 등)

                        return newRow;
                    });
                    setCsvData({data: records, errors, meta});
                }
            })
        }
    }, [text, readString]);

    useEffect(()=>{
        if(open && csvData.data.length > 0) {
            const {records, bindMap, countError}
            = checkTransPlanAndMap(
                null, csvData.data, waveCodeMap,
                vehTypeGroupOrderListByDc.records.map((item)=>item.vehTypeId),
                storeList.records.map((store) => store.storeId),
                comInfoListData.records
                    .filter((com)=>com.dcCd===sessionInfo.user.dscIdAs)
                    .map((com) => com.comId)
            );
            setTableData(records);
            setBindData(Object.values(bindMap));
            setMsgDone('');
            setErrorCount(countError);
            setChanged(true);
        }
        else {
            setTableData([]);
            setBindData([]);
            setErrorCount(0);
            setChanged(false);
        }
    }, [csvData, open, sessionInfo, vehTypeGroupOrderListByDc, storeList, comInfoListData, waveCodeMap]);

    // >>>>>>>>>>>>>>>>> Callbacks <<<<<<<<<<<<<<<<<<<<
    const onCompletedUpload = (data, option) => {
        if(data.transPlanUpload.ok) {
            // 사용자의 입력 데이터 테이블에 반영해서 표시. 사용자가 알 수 있게.
            const {list, bindList} = data.transPlanUpload;
            const planMap = {};
            list.forEach((item) => {
                const key = item.storeId + '-' + item.hocha;
                planMap[key] = item;
            });

            // 운송요청 처리 결과.
            let countOk = 0;
            const newTableData = [...tableData];
            const indexMap = {}; // bindList 결과 반영을 위해 storeId-hocha를 key로 tableData의 index를 저장.
            for(let i=0; i < tableData.length; i++) {
                const row = tableData[i];
                const key = row.storeId + '-' + row.hocha;
                indexMap[key] = i;
                if(planMap[key]) {
                    const item = planMap[key];
                    row.ok = item.ok;
                    if(!item.ok) {
                        row.message = item.message;
                    }
                    else countOk++;
                    newTableData[i] = row;
                }
            }
            const messages = [`운송요청 ${newTableData.length}건 중 ${countOk}건 처리 완료`];
            // 동적요청 처리 결과.
            let bindCountOk = 0;
            if(bindList) {
                bindList.forEach((item) => {
                    const pairs = item.bindText.split('/');
                    let pairOk = true;
                    pairs.forEach((pair) => {
                        if(indexMap.hasOwnProperty(pair)) {
                            const i = indexMap[pair];
                            const row = newTableData[i];
                            if(row.ok) {
                                row.ok = item.ok;
                                if(!item.ok) {
                                    row.message = item.message;
                                    pairOk = false;
                                }
                            }
                        }
                    });
                    if(pairOk) bindCountOk++;
                });
                messages.push( `동적요청 ${bindList.length}그룹 중 ${bindCountOk}그룹 처리 완료`);
            }
            setTableData(newTableData);
            setChanged(false); // 다시 올리지 않도록.
            //enqueueSnackbar(msg, {variant: 'success', autoHideDuration: 1500});
            uploadedRef.current = true; // 상위 컴포넌트에서 reload 하도록.
            setMsgDone('[업로드 완료] ');
            addAlert({
                title: "운송요청 업로드",
                message: messages,
                isError: false
            });
        }
        else addAlert({title: "운송요청 업로드", message: [data.transPlanUpload.message]});
    };

    // ##################### Events #####################

    const onClickUpload = () => {
        const p = {
            transPlanList: tableData.filter((row)=>row.ok)
                                .map((row)=>reformTransPlanToUpload(
                                        row,
                                        sessionInfo.user.dscIdAs,
                                        waveCodeMap)
                                ),
            transPlanBindList: null
        }

        if(bindData.length > 0) {
            const bindList = bindData.map((list) => {
                return {
                    dcCd: sessionInfo.user.dscIdAs,
                    planDate: list[0].planDate,
                    bindText: list.map((sh) => sh.storeId + '-' + sh.hocha).join('/')
                }
            });
            p.transPlanBindList = bindList;
        }
        uploadTransPlan({variables: {transPlanPackage: p}});
    };

    const onClickFindFile = () => {
        if(fileInputRef.current) {
            fileInputRef.current.value='';
            fileInputRef.current.click();
        }
    };

    const onCsvFileRead = (event) => {
        const content = event.target.result;
        // 같은 파일을 읽으면 변화가 일어나지 않음. 그래서 더미를 넣어서 변화를 일으킴.
        const prefix = 'P' + (new Date()).getTime();
        setText(prefix+content);
    };

    const onChooseFile = (event) => {
        if(event.target.files.length > 0) {
            const file = event.target.files[0];
    
            if (file) {
                const reader = new FileReader();
                reader.onload = onCsvFileRead;
                if(useEucKr) reader.readAsText(file, 'euc-kr');
                else reader.readAsText(file); // utf-8
            }
        }
    };

    const alertErrorData = (row) => {
        const lines = row.message.split("\n").filter((line)=>line.length > 0);
        addAlert({title: `운송정보 오류(${row.storeId}-${row.hocha})`, message: lines});
    };

    const onRowDoubleClicked = (row) => {
        if(row.ok) {
            enqueueSnackbar('오류가 없는 항목입니다.', {variant: 'info', autoHideDuration: 1500});
        }
        else alertErrorData(row);
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case 'error': alertErrorData(row); break;
            default: break;
        }
    };

    const askAndClose = async () => {
        if(changed) {
            const confirm =  await openConfirmDialog({
                title: "운송요청업로드",
                content: [
                    '변경된 내용이 있습니다.',
                    '창을 닫으시겠습니까?'
                ]
            });

            if(confirm) {
                onClose(uploadedRef.current);
            }
        }
        else onClose(uploadedRef.current);
    };

    const onClickClose = () => {
        askAndClose();
    };

    const onChangeFilterError = (id, value) => {
        setFilterError(value);
        if(value) setFilterBind(false);
    };

    const onChangeFilterBind = (id, value) => {
        setFilterBind(value);
        if(value) setFilterError(false);
    };

    const canAdd = checkAuth(authTableTree.tree.pr_trans_plan_upload);

    const tools = [
        {id:'error', icon: <NotificationImportantIcon/>, color: AppStyle.mui.error, where: (row) => !row.ok}
    ];

    const loadingAny = responseUpload.loading;

    return(
        <DraggableEditor
            open={open}
            title="운송요청 업로드"
            maxWidth="xl"
            startIcon={<UploadFileIcon fontSize={AppWord.LARGE}/>}
            fullScreen={true}
        >
            <DialogContent
                style={{position: 'relative', padding: 0}}
            >
                <Box style={{position:'absolute', top:0,left:0,bottom:0,right:0}}>
                    <ContentConainer>
                        <ContentHeader>
                            <ControlBox>
                                {
                                    tableData.length > 0
                                    ?
                                    <SearchBox value={searchText} onChangeText={setSearchText}/>
                                    :
                                    <span>파일을 선택해주세요.</span>
                                }
                                
                                <Box style={{marginLeft:10}}>
                                {
                                    errorCount > 0 ?
                                        <VzmCheckbox
                                            dict={{id:'filterError', label:`오류항목(${errorCount}건)만 보기`}}
                                            value={filterError}
                                            onChange={onChangeFilterError}
                                        />
                                    :
                                    tableData.length > 0
                                    ? <span>오류가 발견되지 않았습니다.</span>
                                    : null
                                }
                                </Box>
                                {
                                    tableData.length > 0 &&
                                    <Box style={{
                                        marginLeft:10,
                                        borderLeft:'1px solid #ccc',
                                        padding: '0 10px 0 10px',
                                        }}>
                                        {
                                            bindData.length > 0 ?
                                            <VzmCheckbox
                                                dict={{id:'filterBind', label:`동적(${bindData.length}그룹)만 보기`}}
                                                value={filterBind}
                                                onChange={onChangeFilterBind}
                                            />
                                            :
                                            <span>동적 등록 요청이 없음</span>
                                        }
                                    </Box>
                                }
                            </ControlBox>
                            <ToolBox>
                                {
                                    loadingAny ?
                                    <CircularProgress size={20} /> :
                                    <CountLabel count={countToDisplay} />
                                }
                                <Box style={{
                                    borderLeft:'1px solid #ccc',
                                    height: '100%', marginLeft: 10
                                }}>&nbsp;</Box>
                                <input
                                    type="file"
                                    accept=".csv"
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    onChange={onChooseFile}
                                />
                                {
                                    fileInputRef.current && canAdd &&
                                    <>
                                    <Tooltip
                                        title="한글이 꺠지면 이 설정을 바꿔서 다시 파일찾기를 해보세요."
                                    >
                                        <span style={{marginLeft:10}}>
                                            <VzmCheckbox
                                                dict={{
                                                    id: 'useEucKr', label: '업로드 파일 완성형한글로 읽기',
                                                }}
                                                value={useEucKr}
                                                onChange={(id,value,err) => setUseEucKr(value)}
                                            />
                                        </span>
                                    </Tooltip>
                                    <Button
                                        variant={AppStyle.mui.contained}
                                        color={AppStyle.mui.primary}
                                        startIcon={<FindInPageIcon/>}
                                        onClick={onClickFindFile}
                                        style={{marginLeft:10}}
                                    >
                                        파일 찾기
                                    </Button>
                                    </>
                                }
                            </ToolBox>
                        </ContentHeader>
                        <FlexyTable
                            name={tableProps.tableId}
                            tableProps={tableProps}
                            records={
                                filterError
                                ? tableData.filter((row)=>!row.ok)
                                : filterBind
                                ? tableData.filter((row) => row.ok && row.bindText)
                                : tableData}
                            search={searchText}
                            tools={tools}
                            onClickTool={onClickToolOnRow}
                            onChangeCountToDisplay={setCountToDisplay}
                            onRowDoubleClicked={onRowDoubleClicked}
                        />
                    </ContentConainer>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    variant={AppStyle.mui.contained}
                    color={AppStyle.mui.primary}
                    disabled={loadingAny || !changed}
                    startIcon={<UploadFileIcon/>}
                    onClick={onClickUpload}
                >
                    {errorCount > 0 ? '오류 없는 항목만 ' : ''}
                    업로드
                </Button>
                <Button onClick={onClickClose} color={AppStyle.mui.warning}
                    variant={AppStyle.mui.contained}
                    disabled={loadingAny}
                    startIcon={<CloseIcon/>}
                >
                    닫기
                </Button>
            </DialogActions>
        </DraggableEditor>
    );
}